import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Eye, MessageSquare, Calendar, Edit, ExternalLink, RefreshCcw, AlertCircle, Save, MessageCircle, FileText } from 'lucide-react';
import { YocoCheckoutButton } from '@/components/YocoCheckoutButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEASE_WHATSAPP = '27839858375'; // John Smith & Associates

const MONTHS_12 = (() => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en', { month: 'short', year: 'numeric' });
    months.push({ value, label });
  }
  return months;
})();

const getImageUrl = (photo) => {
  if (!photo) return null;
  const path = photo.storage_path;
  if (path && (path.startsWith('http://') || path.startsWith('https://'))) return path;
  return `${API}/files/${path}`;
};

const StatCard = ({ label, value, sub }) => (
  <div className="border border-gray-300 p-6 bg-paper">
    <div className="caps text-muted mb-2">{label}</div>
    <div className="font-serif text-3xl md:text-4xl text-ink">{value}</div>
    {sub && <div className="text-sm text-muted mt-1 font-serif italic">{sub}</div>}
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    approved: 'bg-ink text-paper',
    pending: 'border border-gray-400 text-ink',
    rejected: 'bg-red-100 text-red-800 border border-red-300',
    active: 'bg-ink text-paper',
    COMPLETE: 'bg-green-100 text-green-800 border border-green-300',
    free: 'bg-purple-100 text-purple-800 border border-purple-300',
  };
  return <span className={`px-2 py-1 caps text-[10px] ${styles[status] || 'border'}`}>{status}</span>;
};

const daysLeft = (l) => (l.expires_at ? Math.ceil((new Date(l.expires_at) - new Date()) / 86400000) : null);
const listingNeedsRenewal = (l) => !l.founding && daysLeft(l) !== null && daysLeft(l) <= 14;

const ListingTerm = ({ listing }) => {
  const d = daysLeft(listing);
  let text = 'Live';
  if (listing.founding) text = 'Founding listing · never expires';
  else if (d !== null && d <= 0) text = 'Expired · offline until renewed';
  else if (d !== null) text = `Live until ${new Date(listing.expires_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  return <div className={`text-xs mb-3 ${d !== null && d <= 0 && !listing.founding ? 'text-red-700' : 'text-muted'}`} data-testid={`listing-term-${listing.id}`}>{text}</div>;
};

const RenewalNotice = ({ listing, user }) => {
  const d = daysLeft(listing);
  const expired = d <= 0;
  return (
    <div className={`${expired ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'} border p-4 mb-4 flex items-center justify-between gap-4 flex-wrap`} data-testid={`renewal-notice-${listing.id}`}>
      <div className="flex items-center gap-3">
        <AlertCircle className={`w-5 h-5 ${expired ? 'text-red-700' : 'text-yellow-700'}`} strokeWidth={1.5} />
        <div>
          <div className="font-serif text-lg text-ink">{expired ? `"${listing.title}" has expired` : `"${listing.title}" expires in ${d} day${d === 1 ? '' : 's'}`}</div>
          <div className="text-sm text-muted">{expired ? 'It is offline. Renew to put it back live for 3 months.' : 'Renew now to keep it live for another 3 months.'}</div>
        </div>
      </div>
      <YocoCheckoutButton
        amountZar={950}
        customerName={user?.name}
        customerEmail={user?.email}
        description="Cape Town Monthly — Listing Renewal (3 months)"
        metadata={{ type: 'renewal', listing_id: listing.id }}
        label="Renew R950"
        testId={`renew-listing-${listing.id}`}
      />
    </div>
  );
};

export const HostDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [editingCalendarFor, setEditingCalendarFor] = useState(null);
  const [calendarMonths, setCalendarMonths] = useState([]);
  const [savingCalendar, setSavingCalendar] = useState(false);

  const openCalendarEdit = (listing) => {
    setEditingCalendarFor(listing);
    setCalendarMonths(listing.available_months || []);
  };

  const toggleCalendarMonth = (m) => {
    setCalendarMonths((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]);
  };

  const saveCalendar = async () => {
    setSavingCalendar(true);
    try {
      await axios.patch(
        `${API}/host/listings/${editingCalendarFor.id}/calendar`,
        { available_months: calendarMonths },
        { withCredentials: true }
      );
      toast.success('Calendar updated');
      setEditingCalendarFor(null);
      fetchAll();
    } catch (error) {
      toast.error('Failed to save calendar');
    } finally {
      setSavingCalendar(false);
    }
  };

  useEffect(() => {
    fetchAll();
    if (searchParams.get('paid')) {
      toast.success('Payment successful! You can now create your listing.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, enqRes, payRes] = await Promise.all([
        axios.get(`${API}/host/dashboard`, { withCredentials: true }),
        axios.get(`${API}/enquiries/host`, { withCredentials: true }),
        axios.get(`${API}/host/payments`, { withCredentials: true }),
      ]);
      setData(dashRes.data);
      setEnquiries(enqRes.data);
      setPayments(payRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-ink"></div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-paper text-ink">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-10 pb-6 border-b" style={{ borderBottomColor: 'var(--line)' }}>
          <div className="flex items-baseline justify-between flex-wrap gap-4">
            <div>
              <div className="caps text-muted mb-2">Host Dashboard</div>
              <h1 className="font-serif text-4xl md:text-5xl" data-testid="host-dashboard-title">
                Welcome, <em className="italic">{user?.name?.split(' ')[0]}</em>
              </h1>
            </div>
            <Button
              onClick={() => navigate(data?.can_create_listing ? '/host/create-listing' : '/list-property')}
              className="btn-editorial btn-solid"
              data-testid="new-listing-btn"
            >
              <Plus className="w-4 h-4" strokeWidth={2} /> {data?.can_create_listing ? 'New Listing' : 'Buy listing slot'}
            </Button>
          </div>
        </div>

        {data?.listings?.filter(listingNeedsRenewal).map((l) => (
          <RenewalNotice key={l.id} listing={l} user={user} />
        ))}

        {/* Lease agreement help */}
        <div className="border border-line-strong bg-paper p-6 mb-8" data-testid="lease-request-card">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-ink flex-shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <div className="font-serif text-lg text-ink">Need a lease agreement drafted?</div>
                <div className="text-sm text-muted max-w-md">John Smith &amp; Associates, our local property attorney, can prepare a professionally drafted lease for R2,500. Tap to open WhatsApp.</div>
              </div>
            </div>
            <a
              href={`https://wa.me/${LEASE_WHATSAPP}?text=${encodeURIComponent("Hi John Smith & Associates — I'm a host on Cape Town Monthly and I'd like to request a lease agreement.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white caps px-6 py-3 text-[11px] inline-flex items-center gap-2"
              data-testid="lease-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2} /> Request via WhatsApp
            </a>
          </div>
        </div>

        {/* No payment */}
        {!data?.can_create_listing && (
          <div className="border-2 border-ink p-8 mb-8 bg-paper">
            <h2 className="font-serif text-2xl mb-3">Get started</h2>
            <p className="text-muted mb-6">Pay the R950 listing fee (or use a discount code) to unlock listing creation.</p>
            <Button onClick={() => navigate('/list-property')} className="btn-editorial btn-solid">
              Continue setup →
            </Button>
          </div>
        )}

        {/* Stats */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <StatCard label="Total Listings" value={data.stats.total_listings} sub={`${data.stats.approved_listings} approved`} />
            <StatCard label="Pending" value={data.stats.pending_listings} sub="Under review" />
            <StatCard label="Total Views" value={data.stats.total_views} sub="Across all listings" />
            <StatCard label="Enquiries" value={data.stats.total_enquiries} sub={`${data.stats.new_enquiries} new`} />
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="listings" className="w-full">
          <TabsList className="mb-6 bg-transparent border-b border-gray-300 w-full justify-start rounded-none h-auto p-0">
            <TabsTrigger value="listings" className="rounded-none caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="host-tab-listings">
              My Listings ({data?.listings?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="enquiries" className="rounded-none caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="host-tab-enquiries">
              Enquiries ({enquiries.length})
            </TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-none caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="host-tab-calendar">
              Calendar
            </TabsTrigger>
            <TabsTrigger value="payments" className="rounded-none caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="host-tab-payments">
              Payments ({payments.length})
            </TabsTrigger>
          </TabsList>

          {/* Listings Tab */}
          <TabsContent value="listings">
            {data?.listings?.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-400">
                <p className="font-serif text-xl italic text-muted mb-4">No listings yet</p>
                <Button
                  onClick={() => navigate(data?.can_create_listing ? '/host/create-listing' : '/list-property')}
                  className="btn-editorial btn-solid"
                >
                  <Plus className="w-4 h-4" strokeWidth={2} /> {data?.can_create_listing ? 'Create your first listing' : 'Buy listing slot to start'}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data?.listings?.map((listing) => (
                  <div key={listing.id} className="border border-gray-300 bg-paper" data-testid={`host-listing-${listing.id}`}>
                    <div className="aspect-[4/3] bg-gray-200 overflow-hidden">
                      {listing.photos?.[0] ? (
                        <img src={getImageUrl(listing.photos[0])} alt={listing.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">No photos yet</div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <StatusBadge status={listing.verification_status} />
                        <span className="caps text-muted text-[10px]">{listing.neighbourhood}</span>
                      </div>
                      <h3 className="font-serif text-xl mb-2">{listing.title}</h3>
                      <ListingTerm listing={listing} />
                      <div className="flex items-center gap-4 text-sm text-muted mb-4">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" strokeWidth={1.5} /> {listing.views || 0}</span>
                        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" strokeWidth={1.5} /> {listing.enquiries_count || 0}</span>
                      </div>
                      <div className="flex justify-between items-center border-t pt-4" style={{ borderTopColor: 'var(--line)' }}>
                        <div className="font-serif text-xl">R{listing.monthly_price?.toLocaleString()}<span className="text-xs text-muted">/mo</span></div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => navigate(`/host/create-listing?edit=${listing.id}`)}
                            className="text-xs caps hover:underline flex items-center gap-1"
                            data-testid={`edit-listing-${listing.id}`}
                          >
                            <Edit className="w-3 h-3" strokeWidth={1.5} /> Edit
                          </button>
                          <button
                            onClick={() => navigate(`/listings/${listing.id}`)}
                            className="text-xs caps hover:underline"
                            data-testid={`view-listing-${listing.id}`}
                          >
                            <ExternalLink className="w-3 h-3 inline" strokeWidth={1.5} /> View
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Enquiries Tab */}
          <TabsContent value="enquiries">
            {enquiries.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-400">
                <p className="font-serif text-xl italic text-muted">No enquiries yet — they&apos;ll appear here when guests reach out</p>
              </div>
            ) : (
              <div className="space-y-3">
                {enquiries.map((e) => (
                  <div
                    key={e.id}
                    className="border border-gray-300 p-5 bg-paper cursor-pointer hover:border-ink transition-colors"
                    onClick={() => setSelectedEnquiry(e)}
                    data-testid={`enquiry-${e.id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-serif text-lg">{e.guest_name}</div>
                        <div className="caps text-muted text-[10px]">{e.listing_title}</div>
                      </div>
                      <div className="text-xs text-muted">{new Date(e.created_at).toLocaleDateString()}</div>
                    </div>
                    <p className="text-sm text-muted line-clamp-2">{e.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <div className="space-y-6">
              {data?.listings?.length === 0 ? (
                <p className="text-center py-16 font-serif italic text-muted">Create a listing first to manage its calendar</p>
              ) : (
                data?.listings?.map((listing) => (
                  <div key={listing.id} className="border border-gray-300 p-6 bg-paper">
                    <div className="flex justify-between items-baseline mb-4">
                      <div>
                        <h3 className="font-serif text-xl">{listing.title}</h3>
                        <div className="caps text-muted text-[10px] mt-1">{listing.neighbourhood}</div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <StatusBadge status={listing.verification_status} />
                        <button
                          onClick={() => openCalendarEdit(listing)}
                          className="text-xs caps hover:underline flex items-center gap-1"
                          data-testid={`edit-calendar-${listing.id}`}
                        >
                          <Edit className="w-3 h-3" strokeWidth={2} /> Edit
                        </button>
                      </div>
                    </div>
                    <div className="caps text-muted text-xs mb-3">Next 12 months</div>
                    <div className="grid grid-cols-6 md:grid-cols-12 gap-1">
                      {MONTHS_12.map((m) => {
                        const isAvailable = listing.available_months?.includes(m.value);
                        return (
                          <div
                            key={m.value}
                            className={`p-2 text-center text-[10px] border ${
                              isAvailable
                                ? 'bg-ink text-paper border-ink'
                                : 'bg-red-50 text-red-800 border-red-200 line-through'
                            }`}
                          >
                            {m.label}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-4 mt-3 text-xs text-muted">
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-ink"></div> Available
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-red-50 border border-red-200"></div> Not available
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            {payments.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-400">
                <p className="font-serif text-xl italic text-muted">No payments yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-300">
                  <thead className="bg-ink/5">
                    <tr className="text-left caps text-muted">
                      <th className="p-3">Payment ID</th>
                      <th className="p-3">Type</th>
                      <th className="p-3">Amount</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Expires</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.m_payment_id} className="border-t border-gray-300">
                        <td className="p-3 font-mono text-xs">{p.m_payment_id?.slice(0, 8)}...</td>
                        <td className="p-3 caps text-xs">{p.type || 'listing'}</td>
                        <td className="p-3 font-serif">R{p.amount}</td>
                        <td className="p-3"><StatusBadge status={p.status} /></td>
                        <td className="p-3 text-xs text-muted">{p.expires_at ? new Date(p.expires_at).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Calendar Edit Modal */}
      <Dialog open={!!editingCalendarFor} onOpenChange={() => setEditingCalendarFor(null)}>
        <DialogContent className="bg-paper border-gray-400 rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Edit calendar
            </DialogTitle>
          </DialogHeader>
          {editingCalendarFor && (
            <div className="space-y-4 pt-2">
              <div className="caps text-muted text-xs">{editingCalendarFor.title}</div>
              <p className="text-sm text-muted">Click months to toggle availability. Guests will only see months marked as available.</p>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {MONTHS_12.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => toggleCalendarMonth(m.value)}
                    className={`p-3 text-sm border transition-colors ${
                      calendarMonths.includes(m.value)
                        ? 'bg-ink text-paper border-ink'
                        : 'border-gray-400 hover:border-ink text-ink'
                    }`}
                    data-testid={`calendar-month-${m.value}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  onClick={saveCalendar}
                  disabled={savingCalendar}
                  className="btn-editorial btn-solid"
                  data-testid="save-calendar-btn"
                >
                  {savingCalendar ? 'Saving...' : 'Save'} <Save className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => setEditingCalendarFor(null)}
                  className="btn-editorial btn-ghost border-l"
                  style={{ borderLeft: '1px solid rgba(17,17,16,0.85)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Enquiry Modal */}
      <Dialog open={!!selectedEnquiry} onOpenChange={() => setSelectedEnquiry(null)}>
        <DialogContent className="bg-paper border-gray-400 rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Enquiry from {selectedEnquiry?.guest_name}</DialogTitle>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-4 pt-2">
              <div className="caps text-muted text-xs">{selectedEnquiry.listing_title}</div>
              <div className="p-4 bg-ink/5 border-l-2 border-ink font-serif italic">{selectedEnquiry.message}</div>
              <div className="space-y-2 text-sm">
                <div><span className="caps text-muted text-xs">Email:</span> <a href={`mailto:${selectedEnquiry.guest_email}`} className="hover:underline">{selectedEnquiry.guest_email}</a></div>
                <div><span className="caps text-muted text-xs">Received:</span> {new Date(selectedEnquiry.created_at).toLocaleString()}</div>
              </div>
              <a
                href={`mailto:${selectedEnquiry.guest_email}?subject=Re: ${selectedEnquiry.listing_title}`}
                className="btn-editorial btn-solid inline-flex mt-4"
                data-testid="reply-enquiry-btn"
              >
                Reply by email →
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
